import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import SEO from '../components/SEO';
import NotFound from './NotFound';
import SectionRenderer from '../components/sections/SectionRenderer';
import { HOME_SECTION_REGISTRY } from '../components/sections/homeSectionRegistry';
import { useCart } from '../context/CartContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { Icon } from '../utils/icons';
import { formatPrice } from '../utils/format';
import './Home.css';
import './ContentPage.css';

const TRUST_ITEMS = [
  { id: 'ce', icon: Icon.Secure },
  { id: 'shipping', icon: Icon.Shipping },
  { id: 'secure', icon: Icon.Lock },
  { id: 'returns', icon: Icon.Returns },
];

const ContentPage = ({ preview = false }) => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const { siteSettings } = useCart();
  const settings = useSiteSettings();
  const [pageData, setPageData] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [loading, setLoading] = useState(!preview);
  const [notFound, setNotFound] = useState(false);
  const [openFaqKey, setOpenFaqKey] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get('/api/products?popular=true&limit=24').catch(() => ({ data: { products: [] } })),
      api.get('/api/categories').catch(() => ({ data: { categories: [] } })),
      api.get('/api/bundles/active').catch(() => ({ data: [] })),
    ]).then(([productResponse, categoryResponse, bundleResponse]) => {
      if (cancelled) return;
      setProducts(productResponse.data?.products || []);
      setCategories(categoryResponse.data?.categories || []);
      setBundles(Array.isArray(bundleResponse.data) ? bundleResponse.data : []);
    });
    try {
      const stored = JSON.parse(localStorage.getItem('recently_viewed') || '[]');
      if (Array.isArray(stored)) setRecentlyViewed(stored.slice(0, 6));
    } catch {
      setRecentlyViewed([]);
    }
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (preview) return undefined;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    api.get(`/api/content/pages/${slug}`, { params: { locale: (i18n.language || 'sq').split('-')[0] } })
      .then((response) => { if (!cancelled) setPageData(response.data); })
      .catch((error) => {
        if (!cancelled) setNotFound(error.response?.status === 404);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [i18n.language, preview, slug]);

  useEffect(() => {
    if (!preview) return undefined;
    const handlePreview = (event) => {
      if (event.origin !== window.location.origin || event.data?.type !== 'STOREFRONT_PAGE_PREVIEW') return;
      setPageData(event.data.payload);
      setLoading(false);
    };
    window.addEventListener('message', handlePreview);
    window.parent?.postMessage({ type: 'STOREFRONT_PAGE_PREVIEW_READY' }, window.location.origin);
    return () => window.removeEventListener('message', handlePreview);
  }, [preview]);

  const freeShipThreshold = parseFloat(siteSettings?.freeShippingThreshold) || 0;
  const currencyLabel = settings.currencySymbol || settings.currency || t('common.currency', 'USD');
  const trustItems = TRUST_ITEMS.filter(({ id }) => id !== 'shipping' || freeShipThreshold > 0).filter(({ id }) => id !== 'returns' || settings.returnsEnabled);
  const trustVars = (id) => {
    if (id === 'shipping') return { amount: formatPrice(freeShipThreshold, { lang: i18n.language }), currency: currencyLabel };
    if (id === 'returns') return { days: settings.returnsWindowDays };
    return {};
  };

  const handleNewsletterSubmit = async (event) => {
    event.preventDefault();
    if (!newsletterEmail.trim()) return;
    setNewsletterSubmitting(true);
    setNewsletterMessage('');
    try {
      await api.post('/api/newsletter/subscribe', { email: newsletterEmail.trim(), source: `page:${pageData?.page?.slug || 'preview'}`, language: i18n.language || 'sq' });
      setNewsletterMessage(t('home.newsletter.success', 'Thanks, you are on the list.'));
      setNewsletterEmail('');
    } catch {
      setNewsletterMessage(t('home.newsletter.error', 'Could not subscribe. Please try again later.'));
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  const layout = useMemo(() => ({ template: { sections: pageData?.sections || [] } }), [pageData]);
  const context = {
    t,
    i18n,
    currency: settings.currency,
    storeName: settings.storeName,
    trustItems,
    trustVars,
    heroCollage: [],
    hasCollage: false,
    featuredProducts: products,
    categories,
    bundles,
    recentlyViewed,
    openFaqKey,
    setOpenFaqKey,
    newsletterEmail,
    setNewsletterEmail,
    newsletterMessage,
    newsletterSubmitting,
    handleNewsletterSubmit,
    isEditorPreview: preview,
    dynamicSources: {
      store: {
        name: settings.storeName,
        email: settings.storeEmail,
        phone: settings.storePhone,
        address: settings.storeAddress,
        nipt: settings.storeNipt,
        whatsAppPhone: settings.whatsAppPhone,
        currency: settings.currency,
      },
      page: {
        title: pageData?.page?.title,
        slug: pageData?.page?.slug,
      },
    },
    contact: {
      email: settings.storeEmail,
      phone: settings.storePhone,
      address: settings.storeAddress,
      nipt: settings.storeNipt,
      whatsAppEnabled: settings.whatsAppEnabled,
      whatsAppPhone: settings.whatsAppPhone,
      mapLat: settings.storeMapLat,
      mapLng: settings.storeMapLng,
    },
  };

  if (notFound) return <NotFound />;
  if (loading || !pageData) return <div className="content-page content-page--loading" aria-busy="true"><div className="container">Loading...</div></div>;

  const page = pageData.page || {};
  return (
    <main className="content-page">
      {!preview && <SEO title={page.metaTitle || page.title} description={page.metaDescription} image={page.metaImage} url={page.canonicalUrl || undefined} noIndex={page.noIndex} />}
      <SectionRenderer layout={layout} registry={HOME_SECTION_REGISTRY} context={context} />
    </main>
  );
};

export default ContentPage;
